import * as DisplayTypes from 'customer-data-filters-ui/filterQueryFormat/DisplayTypes';
import * as OperatorTypes from 'customer-data-filters-ui/converters/objectSeg/ObjectSegOperatorTypes';
import * as UnifiedEventPropertyTypes from 'customer-data-filters-ui/filterQueryFormat/UnifiedEventPropertyTypes';
import getIn from 'transmute/getIn';
import DisplayValueBoolean from 'customer-data-filters-ui/components/display/DisplayValueBoolean';
import DisplayValueCta from 'customer-data-filters-ui/components/display/DisplayValueCta';
import DisplayValueDate from 'customer-data-filters-ui/components/display/DisplayValueDate';
import DisplayValueDuration from 'customer-data-filters-ui/components/display/DisplayValueDuration';
import DisplayValueEmailLink from 'customer-data-filters-ui/components/display/DisplayValueEmailLink';
import DisplayValueEnumeration from 'customer-data-filters-ui/components/display/DisplayValueEnumeration';
import DisplayValueExternalOption from 'customer-data-filters-ui/components/display/DisplayValueExternalOption';
import DisplayValueNumber from 'customer-data-filters-ui/components/display/DisplayValueNumber';
import DisplayValuePercentage from 'customer-data-filters-ui/components/display/DisplayValuePercentage';
import DisplayValueProperty from 'customer-data-filters-ui/components/display/DisplayValueProperty';
import DisplayValueRollingInequality from 'customer-data-filters-ui/components/display/DisplayValueRollingInequality';
import DisplayValueRollingInequalityWithUnit from 'customer-data-filters-ui/components/display/DisplayValueRollingInequalityWithUnit';
import DisplayValueRollingPropertyUpdated from 'customer-data-filters-ui/components/display/DisplayValueRollingPropertyUpdated';
import DisplayValueText from 'customer-data-filters-ui/components/display/DisplayValueText';
import DisplayValueRegex from 'customer-data-filters-ui/components/display/DisplayValueRegex';
import { isExternalOptionsField } from 'customer-data-filters-ui/utils/isExternalOptionsField';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { useContext } from 'react';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import { useStrategy } from '../../hooks/useStrategy';
import { useTranslationHelpers } from '../../hooks/useTranslationHelpers';
import I18n from 'I18n';
import { hasFieldOverrideV2 } from '../../permissions/gates';
import { Map as ImmutableMap } from 'immutable';
import { operatorSupportsExternalOptions } from '../../utils/operatorSupportsExternalOptions';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'refe... Remove this comment to see the full error message
import { TEAM as TeamReferenceObjectType } from 'reference-resolvers/constants/ReferenceObjectTypes';
import { TEAM as TeamExternalOptionType } from 'customer-data-objects/externalOptions/ExternalOptionsReferenceTypes';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
const getPortalTzDate = timestamp => I18n.moment.portalTz(timestamp);
const getUtcTzDate = timestamp => I18n.moment.tz(timestamp, 'UTC');
const getDisplayValue = (fieldType, value) => {
  switch (fieldType) {
    case 'bool':
      return Boolean(value);
    case 'number':
      return Number(value);
    case 'date':
      return getUtcTzDate(Number(value));
    case 'datetime':
      return getPortalTzDate(Number(value));
    default:
      return value;
  }
};
export const getValueComponent = (fieldDefinitions, operator, objectName, getIsUngated) => {
  const field = operator.field;
  const CustomValueComponent = getIn([field.name, 'ValueComponent'], fieldDefinitions);
  if (CustomValueComponent) {
    return CustomValueComponent;
  }
  const fieldType = getIn([field.name, 'inputType'], fieldDefinitions) || getIn([field.type, 'inputType'], fieldDefinitions) || field.displayType || field.type;

  // TEAM reference fields must use a display component that supports a
  // ReferenceResolver even if its configured for external options to allow
  // the UI to use the FilterOperatorTeamInput component
  if ((field === null || field === void 0 ? void 0 : field.referencedObjectType) === TeamReferenceObjectType || (field === null || field === void 0 ? void 0 : field.externalOptionsReferenceType) === TeamExternalOptionType) {
    // If the display type is RollingPropertyUpdatedDisplayType, the value will be a timestamp not a resolvable id
    if (fieldType !== DisplayTypes.RollingPropertyUpdatedDisplayType) {
      return DisplayValueEnumeration;
    }
  }
  if (field.type === 'number' && field.numberDisplayHint === 'duration') {
    return DisplayValueDuration;
  }
  if (field.type === 'number' && field.numberDisplayHint === 'percentage') {
    return DisplayValuePercentage;
  }
  switch (fieldType) {
    case DisplayTypes.BooleanDisplayType:
      return DisplayValueBoolean;
    case DisplayTypes.CtaDisplayType:
      return DisplayValueCta;
    case DisplayTypes.DateDisplayType:
    case DisplayTypes.DatetimeDisplayType:
      return DisplayValueDate;
    case DisplayTypes.EmailLinkDisplayType:
      return DisplayValueEmailLink;
    case DisplayTypes.EmailSubscriptionDisplayType:
    case DisplayTypes.EnumerationDisplayType:
    case UnifiedEventPropertyTypes.OBJECT_COORDINATES:
      return isExternalOptionsField(field, objectName, getIsUngated) && operatorSupportsExternalOptions(operator) ? DisplayValueExternalOption : DisplayValueEnumeration;
    case DisplayTypes.NumberDisplayType:
      return DisplayValueNumber;
    case DisplayTypes.PercentageDisplayType:
      return DisplayValuePercentage;
    case DisplayTypes.PropertyDisplayType:
      return DisplayValueProperty;
    case DisplayTypes.RollingPropertyUpdatedDisplayType:
      return DisplayValueRollingPropertyUpdated;
    case DisplayTypes.RollingInequalityDisplayType:
      return DisplayValueRollingInequality;
    case DisplayTypes.RollingWithUnitDisplayType:
      return DisplayValueRollingInequalityWithUnit;
    case DisplayTypes.RegexString:
      return DisplayValueRegex;
    case DisplayTypes.NoDisplayType:
      return undefined;
    default:
      return DisplayValueText;
  }
};

// Used for rendering the date for "updated on" or "ever equal to" operators; the former
// is evaluated in UTC, the doesn't use time in evaluation
const renderDate = (date, operator, getIsUngated) => {
  return /*#__PURE__*/_jsx(DisplayValueDate, {
    value: getUtcTzDate(date),
    operator: operator,
    getIsUngated: getIsUngated
  });
};
const renderValue = (currencyCode, fieldDefinitions, getFamilyValueResolver, getReferencedObjectType, getSpecialOptionsForReferenceType, objectName, operator, value, getIsUngated) => {
  const ValueComponent = getValueComponent(fieldDefinitions, operator, objectName, getIsUngated);
  const specialOptions = getSpecialOptionsForReferenceType(getReferencedObjectType(objectName, operator), objectName);
  const valueIsEmpty = value === null || value === undefined;
  if (!ValueComponent || valueIsEmpty) {
    return '';
  }
  const fieldType = operator.field.type;
  const displayValue = getDisplayValue(fieldType, value);
  return (
    /*#__PURE__*/
    // @ts-expect-error ts-migrate(2604) FIXME: JSX element type 'ValueComponent' does not have an... Remove this comment to see the full error message
    _jsx(ValueComponent, {
      currencyCode: currencyCode,
      fieldDefinitions: fieldDefinitions,
      getFamilyValueResolver: getFamilyValueResolver,
      getReferencedObjectType: getReferencedObjectType,
      operator: operator,
      specialOptions: specialOptions,
      value: displayValue,
      getIsUngated: getIsUngated
    })
  );
};
const ViewerTestFiltersPropertyValueDisplay = props => {
  const {
    objectName,
    operator,
    testFilterData
  } = props;
  const {
    currencyCode,
    getIsUngated
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    getFieldDefinitions,
    getReferencedObjectType,
    getSpecialOptionsForReferenceType,
    getValueResolver
  } = useStrategy();
  // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
  const {
    getFieldLabelString
  } = useTranslationHelpers();
  const fieldDefinitions = hasFieldOverrideV2(getIsUngated) ? ImmutableMap({}) : getFieldDefinitions(objectName);
  const getFamilyValueResolver = getValueResolver.bind(null, objectName);
  if (!testFilterData) {
    return null;
  }
  const {
    firstMatchingPropertyValue,
    firstNonMatchingPropertyValue,
    operator: operatorType,
    propertyValue,
    status
  } = testFilterData;
  const valueHasEverBeen = operatorType && [OperatorTypes.HAS_EVER_BEEN_ANY_OF, OperatorTypes.HAS_EVER_BEEN_EQUAL_TO, OperatorTypes.HAS_EVER_BEEN_EXACTLY, OperatorTypes.HAS_EVER_CONTAINED, OperatorTypes.HAS_EVER_CONTAINED_ALL].includes(operatorType) && status === 'PASS' && firstMatchingPropertyValue;
  const valueHasNeverBeen = operatorType && [OperatorTypes.HAS_NEVER_BEEN_ANY_OF, OperatorTypes.HAS_NEVER_BEEN_EQUAL_TO, OperatorTypes.HAS_NEVER_BEEN_EXACTLY, OperatorTypes.HAS_NEVER_CONTAINED, OperatorTypes.HAS_NEVER_CONTAINED_ALL].includes(operatorType) && status === 'FAIL' && firstNonMatchingPropertyValue;
  const valueUpdatedOn = operatorType === OperatorTypes.UPDATED_IN_LAST_X_DAYS || operatorType === OperatorTypes.NOT_UPDATED_IN_LAST_X_DAYS;
  let valueForDisplay;
  let message;
  if (valueHasEverBeen) {
    valueForDisplay = firstMatchingPropertyValue;
  } else if (valueHasNeverBeen) {
    valueForDisplay = firstNonMatchingPropertyValue;
  } else {
    valueForDisplay = propertyValue;
  }
  const value = valueForDisplay && valueForDisplay.value;
  const date = valueHasEverBeen || valueHasNeverBeen || valueUpdatedOn ? valueForDisplay && valueForDisplay.timestamp : undefined;
  const valueIsEmpty = value === null || value === undefined;
  if (valueIsEmpty) {
    message = 'customerDataFiltersUI.testFiltersPropertyValue.valueUnknown';
  } else if (valueHasEverBeen || valueHasNeverBeen) {
    message = 'customerDataFiltersUI.testFiltersPropertyValue.valueHasEverBeen';
  } else if (valueUpdatedOn) {
    message = 'customerDataFiltersUI.testFiltersPropertyValue.valueUpdatedOn';
  } else {
    message = 'customerDataFiltersUI.testFiltersPropertyValue.valueKnown';
  }
  const options = {
    date: date && renderDate(date, operator, getIsUngated),
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
    label: getFieldLabelString(operator.field, objectName),
    objectType: objectName,
    value: renderValue(currencyCode, fieldDefinitions, getFamilyValueResolver, getReferencedObjectType, getSpecialOptionsForReferenceType, objectName, operator, value, getIsUngated)
  };
  return /*#__PURE__*/_jsx(View, {
    marginInlineStart: "300",
    as: "div",
    children: /*#__PURE__*/_jsx("b", {
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: message,
        options: options
      })
    })
  });
};
export default ViewerTestFiltersPropertyValueDisplay;